import React from "react";
import { motion } from "framer-motion";
import {
  Globe2,
  Smartphone,
  MessagesSquare,
  ArrowUpRight,
  Apple,
  Check,
} from "lucide-react";

const PAYMENT_IMG =
  "https://images.pexels.com/photos/6214369/pexels-photo-6214369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = [
  {
    id: "web",
    title: "Websites that convert",
    desc: "High-performance, SEO-ready sites with payments, booking flows and analytics — coded for India's mobile-first internet.",
    Icon: Globe2,
    accent: "#FF7A00",
    bullets: [
      "Next-gen design system",
      "Razorpay / UPI integrated",
      "PageSpeed 95+ on 3G",
    ],
    span: "md:col-span-7 md:row-span-2",
    mockup: "/services/website.png",
    mockupAlt: "Sample website mockup on a laptop",
    mockupClass: "absolute right-[-12%] bottom-[-12%] w-[78%] md:w-[68%] rotate-[-4deg]",
    mockupRing: "from-[#FF7A00]/30 to-transparent",
  },
  {
    id: "apps",
    title: "Native Android & iOS apps",
    desc: "Single-codebase apps deployed to Play Store and App Store with push, deep links and offline-first sync.",
    Icon: Smartphone,
    accent: "#00C853",
    bullets: ["Play Store + App Store", "Push & deep links", "Offline-first"],
    span: "md:col-span-5",
    iosBadge: true,
    mockup: "/services/apps.png",
    mockupAlt: "Sample mobile app mockup on Android & iOS",
    mockupClass: "absolute right-[-14%] bottom-[-18%] w-[72%] rotate-[3deg]",
    mockupRing: "from-[#00C853]/30 to-transparent",
  },
  {
    id: "ai",
    title: "AI Chat Boards",
    desc: "Trained-on-your-business assistants for WhatsApp, web and IVR. 24×7 in Hindi, English & 10 Indic languages.",
    Icon: MessagesSquare,
    accent: "#FF9933",
    bullets: [
      "10+ Indic languages",
      "Lead capture + CRM sync",
      "WhatsApp Business API",
    ],
    span: "md:col-span-5",
  },
];

const Card = ({ s, index }) => {
  const Icon = s.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className={`group relative rounded-3xl p-7 md:p-9 dark:glass-dark glass-light overflow-hidden transition-all duration-500 hover:-translate-y-1 ${s.span}`}
      data-testid={`service-card-${s.id}`}
    >
      {/* Accent gradient blob */}
      <div
        className="absolute -top-20 -right-20 h-56 w-56 rounded-full blur-3xl opacity-40 group-hover:opacity-70 transition-opacity"
        style={{ background: s.accent }}
      />

      {/* Sample mockup image */}
      {s.mockup && (
        <>
          <motion.div
            aria-hidden
            className={`pointer-events-none absolute -bottom-10 -right-10 h-72 w-72 rounded-full blur-3xl bg-gradient-to-br ${s.mockupRing} opacity-60 group-hover:opacity-90 transition-opacity duration-500`}
          />
          <motion.img
            src={s.mockup}
            alt={s.mockupAlt}
            loading="lazy"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className={`pointer-events-none select-none drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)] group-hover:scale-[1.03] group-hover:rotate-0 transition-transform duration-700 ${s.mockupClass}`}
            data-testid={`service-mockup-${s.id}`}
          />
        </>
      )}

      <div className="relative flex flex-col h-full gap-6">
        <div className="flex items-start justify-between">
          <div
            className="h-14 w-14 rounded-2xl grid place-items-center"
            style={{
              background: `${s.accent}1f`,
              border: `1px solid ${s.accent}40`,
            }}
          >
            <Icon className="h-7 w-7" style={{ color: s.accent }} />
          </div>
          <ArrowUpRight className="h-6 w-6 text-muted-foreground group-hover:text-foreground group-hover:rotate-12 transition-all" />
        </div>

        <div className="space-y-3">
          <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">
            {s.title}
          </h3>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
            {s.desc}
          </p>
        </div>

        <div className="mt-auto space-y-2 pt-4">
          {s.bullets.map((b) => (
            <div
              key={b}
              className="flex items-center gap-2.5 text-sm font-medium"
            >
              <span
                className="h-5 w-5 rounded-full grid place-items-center"
                style={{ background: `${s.accent}26` }}
              >
                <Check
                  className="h-3 w-3"
                  style={{ color: s.accent }}
                  strokeWidth={3}
                />
              </span>
              <span>{b}</span>
            </div>
          ))}
        </div>

        {s.iosBadge && (
          <div className="absolute bottom-7 right-7 flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground/5 border border-border text-[11px] font-semibold tracking-wide">
            <Apple className="h-3.5 w-3.5" />
            iOS
            <span className="opacity-40">·</span>
            <span>Android</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section
      id="services"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-7 space-y-5"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A00]" />
              What we ship
            </div>
            <h2
              className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]"
              data-testid="services-heading"
            >
              Three rails. <span className="text-gradient-saffron">One</span>{" "}
              digital identity.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-5 text-base md:text-lg text-muted-foreground"
          >
            Modern websites, native apps and AI chat boards — designed,
            deployed, and live within 48 hours of kickoff.
          </motion.p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {SERVICES.map((s, i) => (
            <Card key={s.id} s={s} index={i} />
          ))}

          {/* Image bento card */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="md:col-span-7 rounded-3xl overflow-hidden relative group min-h-[260px]"
            data-testid="services-payment-card"
          >
            <img
              src={PAYMENT_IMG}
              alt="Digital payments"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/40 to-transparent" />
            <div className="relative h-full p-7 md:p-10 flex flex-col justify-end text-white">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF9933] font-semibold mb-3">
                Payments-ready
              </div>
              <div className="font-heading font-bold text-2xl md:text-3xl max-w-md tracking-tight leading-tight">
                UPI · cards · COD — every Indian payment rail wired in by
                default.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;
